import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EngagementView from '../components/shared/EngagementView';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import { ProvenanceBadge, provenanceOrigin } from '../components/shared/provenance';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import RecordActionsCard from '../components/workflow/RecordActionsCard';
import { DeleteModal } from './QMEngagements';
import { EngagementModal, EngagementNewPage } from './QMEngagementForm';
import { fetchJSON } from '../utils/api';
import { nowLabel } from '../components/shared/NowName';
import { engagementTag } from '../utils/engagementName';

// Management — 一筆 engagement 的詳情頁。
//
// `EngagementView` 是共用元件，`/my/engagements/:id`（唯讀、給團隊看）用的是
// 同一支。這頁只是給管理面自己的網址與動作列。
//
// 有網址帶來的實際好處:workflow 詳情頁的「Open in Management →」直接連
// `/qm/engagements/:id`（見 utils/engagementLink），不必用清單加搜尋關鍵字
// 逼近那一筆（撞名會列出多筆、改名就失效）。
//
// 編輯／刪除仍走既有的 modal（從清單頁 export 過來共用）,因為它們本來就沒有
// 問題——換掉的是「怎麼打開一筆」,不是「怎麼改一筆」。
const QMEngagementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [engagement, setEngagement] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // EngagementView 內部自己抓附件/歷程;存檔後換 key 逼它重讀。
  const [viewKey, setViewKey] = useState(0);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await fetchJSON(`/api/engagements/${id}`);
      setEngagement(data.engagement);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // 新增模式:沒有底層記錄,直接開空白表單。它是 FormShell 全頁
  // （跟 Engagement Registration／Amendment 同一種版面），不是浮在空白頁上的
  // modal;表單本體與編輯 Modal 共用同一份欄位（QMEngagementForm）。
  if (isNew) return <EngagementNewPage />;

  return (
    <RequestDetailShell
      backTo="/qm/engagements"
      backLabel="Engagements"
      loading={!engagement}
      error={error}
      notice={notice}
    >
      {engagement && (
        <>
          <RequestHeader
            requestNo={nowLabel(engagement.entityName || engagement.engagementName, engagement.entityNameNow)}
            typeLabel={['Engagement', engagementTag(engagement.serviceProvided, engagement.engagementSeq, engagement.serviceDescription)]
              .filter(Boolean).join(' · ')}
            originLabel={engagement.sourceRequestNo
              ? <>registered by <WorkflowRefLink refNo={engagement.sourceRequestNo} className="text-sm" /></>
              : provenanceOrigin(engagement.source)}
            submittedAt={engagement.createdAt}
            badge={<ProvenanceBadge source={engagement.source} />}
          />

          <RecordActionsCard
            onEdit={() => setEditing(true)}
            onDelete={() => setDeleting(true)}
          />

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
            <EngagementView
              key={viewKey}
              engagement={engagement}
              basePath={`/api/engagements/${engagement.id}`}
            />
          </div>

          {editing && (
            <EngagementModal
              engagement={engagement}
              onClose={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                setNotice('Engagement updated.');
                setViewKey((k) => k + 1);
                load();
              }}
              onAttachmentsChanged={() => setViewKey((k) => k + 1)}
            />
          )}
          {deleting && (
            <DeleteModal
              engagement={engagement}
              onClose={() => setDeleting(false)}
              onDeleted={() => navigate('/qm/engagements')}
            />
          )}
        </>
      )}
    </RequestDetailShell>
  );
};

export default QMEngagementDetail;
